import type { VariantProps } from "class-variance-authority";
import React, { PropsWithChildren, ReactNode } from "react";
import { Button, buttonVariants } from "@components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";
import { cn } from "@lib/utils";

export type ButtonWithTooltipProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  } & PropsWithChildren & { tooltipContent?: string | null | ReactNode };

/**
 * ButtonWithTooltip is a composition of design-system Button and Tooltip.
 *
 * Business context:
 * - Used where an action must remain compact (icon-only or short label) but still be discoverable.
 * - Common use-case: contextual actions inside a dense UI (e.g., close button in alerts, table row actions).
 * - Improves accessibility/UX by exposing a human-readable explanation without expanding layout.
 *
 * Contract:
 * - Renders a "button" (or Slot when asChild=true) that shows a tooltip on hover/focus.
 * - Accepts all Button variant/size options via buttonVariants (consistent look across app).
 * - tooltipContent should be a short, localized string (e.g., tCommon("words.verbs.close")).
 *
 * Integration notes:
 * - TooltipTrigger is used with asChild to prevent extra DOM wrapper, so Button receives focus/aria directly.
 * - Keep tooltip text stable to avoid layout shift; avoid dynamic content that re-renders on hover.
 * - Prefer concise labels; truncate long strings at the i18n source if needed.
 * - Disabled buttons are wrapped so tooltips still work; size/className stay on the Button
 *   (not on the wrapper) to avoid geometry jumps when toggling disabled.
 * - When the wrapper sits in a ButtonGroup, rounding and the shared left border are forwarded
 *   to the nested Button so a disabled control does not fall out of the group.
 */
export const ButtonWithTooltip = ({
  tooltipContent,
  children,
  disabled,
  className,
  ...props
}: ButtonWithTooltipProps) => (
  <Tooltip>
    {/* Disabled buttons do not receive pointer events; wrap so tooltip still works. */}
    <TooltipTrigger asChild>
      {disabled ? (
        <span
          className={cn(
            "inline-flex h-full min-h-0",
            // ButtonGroup стилизует только прямых детей. Обёртка для tooltip
            // перехватывает rounding/border — прокидываем их на саму кнопку
            // (LKO-95, ретест № 4: неактивная кнопка выпадает из группы).
            "[[data-slot=button-group]_&:not(:first-child)>[data-slot=button]]:rounded-l-none",
            "[[data-slot=button-group]_&:not(:first-child)>[data-slot=button]]:border-l-0",
            "[[data-slot=button-group]_&:not(:last-child)>[data-slot=button]]:rounded-r-none",
          )}
        >
          <Button {...props} disabled className={cn(className, "pointer-events-none")}>
            {children}
          </Button>
        </span>
      ) : (
        <Button {...props} disabled={disabled} className={className}>
          {children}
        </Button>
      )}
    </TooltipTrigger>

    {/* Keep the tooltip purely presentational: avoid interactive controls inside.
       For dynamic labels, memoize upstream to prevent flicker on open/close */}
    {tooltipContent !== undefined && tooltipContent !== null && (
      <TooltipContent>{tooltipContent}</TooltipContent>
    )}
  </Tooltip>
);
